import {Component, inject, Input} from '@angular/core';
import {AsyncPipe, DatePipe} from "@angular/common";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {Observable} from "rxjs";
import {ColDef, GridApi, GridReadyEvent, SizeColumnsToFitGridStrategy} from "ag-grid-community";
import {FormatBoolean} from "../../cell-renderers/show-played/format-boolean.component";
import {AgGridAngular} from "ag-grid-angular";
import {TestimonialsService} from "../../../services/testimonials.service";
import {IGameTestimonials} from "../../../interfaces/IGameTestimonials";
import {TestimonialsSettingsComponent} from "../testimonial-settings/testimonials-settings.component";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";

@Component({
  selector: 'app-testimonials-table',
  standalone: true,
  imports: [
    AgGridAngular,
    AsyncPipe
  ],
  templateUrl: './testimonials-table.component.html',
  styleUrl: './testimonials-table.component.scss'
})
export class TestimonialsTableComponent {

  @Input() set gameSetter(gameP: IGameQueryResult) {
    this.fetchTestimonials(gameP);
  }

  private datePipe: DatePipe = inject(DatePipe);
  private testimonialsService: TestimonialsService = inject(TestimonialsService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  public defaultColDef: ColDef = {
    flex: 1,
    cellDataType: false,
  };
  public themeClass: string =
    "ag-theme-quartz";
  public uiState = UIStateEnum.ShowLoading;
  public uiStateForTemplate = UIStateEnum;
  public testimonialsForGame$: Observable<IGameTestimonials[]> = this.testimonialsService.selectGameTestimonials$();
  public testimonialsGridApi!: GridApi<IGameTestimonials>;
  public autoSizeStrategy: SizeColumnsToFitGridStrategy = {
    type: 'fitGridWidth'
  };

  public testimonialsDefs: ColDef[] = [
    {
      headerName: 'Name of User',
      field: 'GivenByUserName',
    },
    {
      headerName: 'Position of User',
      field: 'GivenByUserPosition'
    },
    {
      headerName: 'Testimonial',
      field: 'Content',
      // Truncated for the cell, full text in the grid's own tooltip. Not a cellRenderer: the content is author-supplied.
      tooltipField: 'Content',
      valueFormatter: params => {
        const full_text = params.value ?? '';
        return full_text.length > 50 ? `${full_text.substring(0, 50)}...` : full_text;
      }
    },
    {
      headerName: 'Created On Date',
      maxWidth: 140,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y');
        return convertedDate ? convertedDate : '';
      },
      field: 'CreatedOn'
    },
    {
      headerName: 'Active',
      maxWidth: 80,
      cellRenderer: FormatBoolean,
      field: 'Active',
    },
    {
      headerName: 'Edit',
      // Pinned and fixed so the edit pencil is always on screen.
      pinned: 'right',
      lockPinned: true,
      // flex: 0 is load-bearing: defaultColDef sets flex: 1, and AG Grid lets flex win over width, so the
      // width and minWidth below are inert without it.
      flex: 0,
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      resizable: false,
      sortable: false,
      suppressSizeToFit: true,
      cellRenderer: TestimonialsSettingsComponent
    }
  ];

  fetchTestimonials(gameP: IGameQueryResult): void {
    this.testimonialsService.fetchTestimonials(gameP.Id).subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
      }, error: (error) => {
        this.errorHandlingService.displayPageLevelErrorMessage(error);
        this.uiState = UIStateEnum.ShowData;
      }
    })
  }

  onTestimonialGridReady(params: GridReadyEvent<IGameTestimonials>) {
    this.testimonialsGridApi = params.api;
    // No sizeColumnsToFit here: autoSizeStrategy already fits the columns, and its defaultMinWidth fought
    // with the 80px Active and Edit columns, overflowing the container.
  }
}
